import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Plus, Check, Layers } from "lucide-react";

const PROFICIENCY_COLORS = ["", "bg-gray-300", "bg-blue-300", "bg-blue-500", "bg-purple-500", "bg-green-500"];

export default function MesCompetences() {
  const { t } = useTranslation();
  const profLabel = (n) => t(`portal.skills.proficiency.${n}`, { defaultValue: "" });
  const sourceLabel = (s) => t(`portal.skills.source.${s}`, { defaultValue: s });
  const [mySkills, setMySkills] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLevel, setEditLevel] = useState(3);
  const [showAdd, setShowAdd] = useState(false);
  const [addSkillId, setAddSkillId] = useState("");
  const [addLevel, setAddLevel] = useState(3);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mine, cat] = await Promise.all([
        apiClient.get("v1/skills/me").catch(() => ({ data: [] })),
        apiClient.get("v1/skills/categories").catch(() => ({ data: [] })),
      ]);
      setMySkills(mine.data || []);
      const cats = cat.data || [];
      setCatalog(cats.flatMap(c => (c.skills || []).map(s => ({ ...s, category_name: c.name }))));
      setCategories(cats);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (skillId, level) => {
    setSaving(true);
    try {
      await apiClient.put("v1/skills/me", { skill_id: skillId, proficiency_level: level, source: "self_declared" });
      setEditingId(null);
      setShowAdd(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  // Group by category
  const grouped = {};
  mySkills.forEach(s => {
    const cat = s.skill?.category?.name || t("portal.skills.otherCategory");
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(s);
  });

  const ownedSkillIds = new Set(mySkills.map(s => s.skill_id));
  const availableToAdd = catalog.filter(s => !ownedSkillIds.has(s.id));

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("common.loading")}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t("portal.skills.intro")}
        </p>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-card rounded-xl border p-4 space-y-3">
          <h4 className="text-sm font-semibold">{t("portal.skills.addTitle")}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select value={addSkillId} onValueChange={setAddSkillId}>
              <SelectTrigger><SelectValue placeholder={t("portal.skills.choosePlaceholder")} /></SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <React.Fragment key={cat.id}>
                    {(cat.skills || []).filter(s => !ownedSkillIds.has(s.id)).map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{cat.name} / {s.name}</SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(addLevel)} onValueChange={v => setAddLevel(parseInt(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} — {profLabel(n)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => handleSave(parseInt(addSkillId), addLevel)} disabled={!addSkillId || saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              {t("common.save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      )}

      {Object.keys(grouped).length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <Layers className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p>{t("portal.skills.empty")}</p>
          <p className="text-xs mt-1">{t("portal.skills.emptyHint")}</p>
        </div>
      )}

      {Object.entries(grouped).map(([category, skills]) => (
        <div key={category} className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{category}</h3>
          <div className="space-y-2">
            {skills.map((es) => (
              <div key={es.id} className="bg-card rounded-xl border px-4 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{es.skill?.name || `#${es.skill_id}`}</div>
                  <div className="text-xs text-muted-foreground">{sourceLabel(es.source)}</div>
                </div>

                {editingId === es.id ? (
                  <div className="flex items-center gap-2">
                    <Select value={String(editLevel)} onValueChange={v => setEditLevel(parseInt(v))}>
                      <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} — {profLabel(n)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8" onClick={() => handleSave(es.skill_id, editLevel)} disabled={saving}>
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingId(null)}>✕</Button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditingId(es.id); setEditLevel(es.proficiency_level); }}
                    className="flex items-center gap-2 group"
                  >
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(n => (
                        <div
                          key={n}
                          className={`w-5 h-2 rounded-sm transition-colors ${n <= es.proficiency_level ? PROFICIENCY_COLORS[es.proficiency_level] : "bg-muted"}`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                      {profLabel(es.proficiency_level)}
                    </span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
